import React, { useState, useEffect, useLayoutEffect, useRef, FC } from 'react';

import { useAppDispatch } from '../redux/hooks';

import { createTierRequested } from '../redux/reducers/tier.slice';
import { useMetaMask } from '../context/MetaMaskContext';

import TierAPIService from '../services/tier.service';
import { ReactComponent as ImageHolderSVG } from '../svg/image-holder.svg';

import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

type CreateTierProps = {
  project: Project;
  toggleCreateTier: () => void;
};

const service = new TierAPIService();

const CreateTier: FC<CreateTierProps> = ({ project, toggleCreateTier }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({});
  const dispatch = useAppDispatch();
  const { metaMask } = useMetaMask();

  const inputRef = useRef<null | HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<null | File>(null);
  const [fileName, setFileName] = useState<string>('');

  function createTier({ name, quantity, price }: { name: string; quantity: string; price: string }) {
    if (!fileName) return;

    const numberWithCommas = quantity.split(',').join('');
    const tier: Tier = { name, quantity: +numberWithCommas, fileName, price };

    dispatch(createTierRequested({ projectId: Number(project?.id), tier }));
    reset();
    toggleCreateTier();
  }

  const transform = {
    input: (value: any) => {
      if (typeof value !== 'string') return;

      const numWithoutComma = Number(value.replace(/[^0-9]/g, ''));
      return isNaN(numWithoutComma) ? '' : value.toString();
    },
    output: (value: any) => {
      if (typeof value !== 'string') return;

      const numWithoutComma = Number(value.replace(/[^0-9]/g, ''));
      return numWithoutComma.toLocaleString('en-US');
    },
  };

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.files?.length || !project?.id) return;

    const file = event.target.files[0];

    setSelectedFile(file);

    const toastLoading = toast.loading('Uploading');

    try {
      const uploadedImage = await service.getSignedUrl(project?.id, file.name);

      await service.uploadBySignedUrl(uploadedImage.signedUrl, file);

      setFileName(uploadedImage.fileName);

      toast.success('Tier image uploaded successfully');
    } catch (error) {
      console.log(error);
    }

    toast.remove(toastLoading);
  }

  return (
    <form onSubmit={handleSubmit(createTier)}>
      <div className="nft-tier">
        <div className="nft-tier--item">
          <div className="nft-tier--item-img" onClick={() => inputRef.current?.click()}>
            {/* <img src="/poster.png" /> */}
            {selectedFile && <img src={URL.createObjectURL(selectedFile)} />}

            {!selectedFile && (
              <div
                style={{
                  width: 64,
                  height: 64,
                  background: '#34373A',
                  border: '1px dashed #AAAAAA',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <div>
                  <ImageHolderSVG width="16px" height="16px" />
                </div>

                <span
                  style={{
                    fontSize: 8,
                    marginTop: 5,
                  }}
                >
                  Upload
                </span>
                <span
                  style={{
                    fontSize: 8,
                  }}
                >
                  (.png, .gif)
                </span>
              </div>
            )}

            <input
              style={{ display: 'none' }}
              type="file"
              onChange={handleChange}
              ref={inputRef}
              accept="image/*,.jpg,.png,.jpeg"
            />
          </div>
          <div className="nft-tier--item-info">
            <div className="app-input" style={{ marginBottom: 8 }}>
              <input placeholder="Name" autoComplete="off" type="text" {...register('name', { required: true })} />
              <span style={{ marginLeft: 4, flex: 1 }}>{errors.name && <small>Name is required.</small>}</span>
            </div>
            <div className="app-input" style={{ marginBottom: 8 }}>
              {/* <input placeholder="Quantity" type="text" {...register('quantity', { required: true })} /> */}
              <Controller
                defaultValue=""
                control={control}
                name="quantity"
                render={({ field }) => (
                  <input
                    type="text"
                    onChange={(e) => field.onChange(transform.input(e.target.value))}
                    value={transform.output(field.value)}
                    onFocus={(event) => event.target.select()}
                  />
                )}
              />
              <span style={{ marginLeft: 4, flex: 1 }}>{errors.quantity && <small>Quantity is required.</small>}</span>
            </div>
            <div className="app-input" style={{ marginBottom: 8 }}>
              <input
                placeholder={`Price on ${metaMask.chainToken}`}
                autoComplete="off"
                type="text"
                {...register('price', { required: true })}
              />
              <span style={{ marginLeft: 4, flex: 1 }}>{errors.price && <small>Price is required.</small>}</span>
            </div>
            <button
              type="submit"
              className={fileName ? 'app-btn color-primary' : 'app-btn color-primary disable'}
              style={{
                height: 24,
                padding: 0,
                fontSize: 12,
              }}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateTier;
